import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Twitter, Mail, ArrowRight, BadgeCheck } from "lucide-react";

const MISSION =
  "The Titan Standard exists to provide independent Tennessee Titans analysis, news, " +
  "and fan engagement all over the world for passionate Titans fans.";

export default function About() {
  const [authors, setAuthors] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/authors");
        setAuthors(data || []);
      } catch (err) {
        if (process.env.NODE_ENV === "development") console.error("[About]", err);
      }
    })();
  }, []);

  return (
    <div data-testid="about-page" className="max-w-[1100px] mx-auto px-5 md:px-8 py-16">
      <div className="font-cond uppercase tracking-[0.3em] text-xs text-titans-blue mb-3">About</div>
      <h1 className="font-display text-5xl md:text-7xl uppercase tracking-wide section-title">
        The Titan Standard
      </h1>

      {/* MISSION */}
      <div className="mt-12 titans-card p-8 md:p-10 border-2 border-titans-blue/40">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-block w-6 h-px bg-titans-red" />
          <span className="font-cond uppercase tracking-[0.22em] text-[10px] text-titans-red">
            Mission Statement
          </span>
        </div>
        <p className="font-display text-2xl md:text-4xl uppercase leading-tight">
          {MISSION}
        </p>
      </div>

      {/* WHY */}
      <div className="mt-16 grid md:grid-cols-3 gap-6">
        {[
          { title: "Independent", body: "No corporate slop. No team-PR talking points. Just real fan-driven coverage from people who watch every snap." },
          { title: "Worldwide", body: "Two-Tone Blue Nation isn't confined to Nashville. We write for every Titans fan, from Tennessee to Tokyo." },
          { title: "Engaged", body: "Trivia, daily games, hot-take threads, fan confidence meters — the site IS the community." },
        ].map((c) => (
          <div key={c.title} className="titans-card p-6">
            <div className="font-display text-xl uppercase tracking-wide text-titans-blue mb-2">{c.title}</div>
            <p className="text-titans-silver text-sm font-body leading-relaxed">{c.body}</p>
          </div>
        ))}
      </div>

      {/* AUTHORS */}
      <div className="mt-20">
        <div className="font-cond uppercase tracking-[0.3em] text-xs text-titans-blue mb-2">The Staff</div>
        <h2 className="font-display text-3xl md:text-4xl uppercase tracking-wide section-title">Who We Are</h2>

        {/* Publisher verification callout — always visible, anchors E-E-A-T. */}
        <div
          className="mt-6 rounded-xl bg-titans-void/40 border border-[#1D9BF0]/30 p-4 md:p-5 flex flex-wrap items-start gap-4"
          data-testid="about-verified-callout"
        >
          <div className="shrink-0 w-11 h-11 rounded-full bg-[#1D9BF0] text-white flex items-center justify-center">
            <BadgeCheck size={22} strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="font-cond uppercase tracking-[0.18em] text-[10px] text-emerald-400 font-bold flex items-center gap-2">
              Verified Publisher
              <span
                className="inline-flex items-center gap-1 px-1.5 py-[2px] rounded-full bg-[#1D9BF0] text-white font-cond font-bold uppercase tracking-[0.14em] text-[9px] leading-none"
                data-testid="about-verified-badge"
              >
                <BadgeCheck size={10} strokeWidth={2.5} /> Verified
              </span>
            </div>
            <div className="font-display text-lg md:text-xl uppercase mt-1 text-white leading-tight">
              The Titan Standard is a registered independent publisher.
            </div>
            <p className="text-titans-silver text-sm font-body mt-2 leading-relaxed">
              Verified via public business registration in Davidson County, Tennessee. Editorial and corrections
              inboxes are actively monitored and answered within one business day. See our{" "}
              <Link to="/ownership" className="text-titans-blue underline">Ownership disclosure</Link>,{" "}
              <Link to="/editorial-standards" className="text-titans-blue underline">Editorial Standards</Link>,{" "}
              and <Link to="/corrections" className="text-titans-blue underline">Corrections Policy</Link>.
            </p>
          </div>
        </div>

        {authors.length === 0 ? (
          <div className="mt-6 grid md:grid-cols-2 gap-5">
            <div className="titans-card p-5" data-testid="about-author-jericho-nelson">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-titans-void border-2 border-titans-blue/50 flex items-center justify-center font-display text-titans-blue text-2xl">
                  JN
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <div className="font-display text-xl uppercase leading-none text-white">Jericho Nelson</div>
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-[2px] rounded-full bg-[#1D9BF0] text-white font-cond font-bold uppercase tracking-[0.14em] text-[9px] leading-none"
                      title="Verified publisher — identity confirmed via business registration"
                      data-testid="about-verified-badge"
                    >
                      <BadgeCheck size={10} strokeWidth={2.5} /> Verified
                    </span>
                  </div>
                  <div className="text-titans-silver text-xs font-cond uppercase tracking-wider mt-1">Founder &amp; Editor-in-Chief</div>
                </div>
              </div>
              <p className="text-titans-silver text-sm font-body mt-3">
                Nashville-based Titans lifer covering the franchise since 2018. Reports on training camp, roster construction, and the salary-cap math behind every big move.
              </p>
              <div className="mt-3 flex items-center gap-3 text-xs font-cond uppercase tracking-wider">
                <a href="mailto:editor@thetitanstandard.com" className="text-titans-blue">editor@thetitanstandard.com</a>
              </div>
            </div>
            <div className="titans-card p-5 border border-emerald-500/25">
              <div className="font-cond uppercase tracking-[0.16em] text-[10px] text-emerald-400 font-bold">Reader-supported</div>
              <div className="font-display text-xl uppercase mt-1 text-white">Independent Newsroom</div>
              <p className="text-titans-silver text-sm font-body mt-2 leading-relaxed">
                We are not affiliated with the Tennessee Titans, the NFL, or any team-adjacent business. All coverage is fan-driven and reader-funded — no PR access, no gifted credentials, no strings.
              </p>
              <div className="mt-3 flex items-center gap-3 text-xs font-cond uppercase tracking-wider">
                <Link to="/editorial-standards" className="text-titans-blue">Editorial Standards</Link>
                <Link to="/ownership" className="text-titans-blue">Ownership</Link>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
            {authors.map((a) => (
              <Link
                key={a.slug}
                to={`/author/${a.slug}`}
                data-testid={`about-author-${a.slug}`}
                className="titans-card p-5 hover-rise group"
              >
                <div className="flex items-center gap-4">
                  {a.photo ? (
                    <img src={a.photo} alt={a.name} className="w-16 h-16 object-cover border-2 border-titans-blue/50 rounded-full" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-titans-void border-2 border-titans-blue/30 flex items-center justify-center font-display text-titans-blue text-2xl">
                      {a.name?.[0] || "?"}
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="font-display text-xl uppercase leading-none text-white group-hover:text-titans-blue transition-colors">{a.name}</div>
                      {a.verified && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-[2px] rounded-full bg-[#1D9BF0] text-white font-cond font-bold uppercase tracking-[0.14em] text-[9px] leading-none"
                          title="Verified publisher"
                        >
                          <BadgeCheck size={10} strokeWidth={2.5} /> Verified
                        </span>
                      )}
                    </div>
                    {a.role && <div className="text-titans-silver text-xs font-cond uppercase tracking-wider mt-1">{a.role}</div>}
                  </div>
                </div>
                {a.bio && <p className="text-titans-silver text-sm font-body mt-3 line-clamp-3">{a.bio}</p>}
                <div className="mt-3 flex items-center gap-3 text-xs font-cond uppercase tracking-wider">
                  {a.twitter && <span className="text-sky-400 inline-flex items-center gap-1"><Twitter size={11} /> {a.twitter}</span>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* CONTACT */}
      <div className="mt-20 titans-card p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-l-4 border-titans-red">
        <div>
          <div className="font-cond uppercase tracking-[0.3em] text-xs text-titans-blue mb-2">Get in touch</div>
          <h3 className="font-display text-3xl uppercase">Got a tip, pitch, or correction?</h3>
        </div>
        <a
          href="mailto:hello@thetitanstandard.com"
          data-testid="about-contact-email"
          className="bg-titans-red hover:bg-titans-red-hover text-white px-6 py-3 font-cond uppercase tracking-wider text-sm inline-flex items-center gap-2"
        >
          <Mail size={14} /> hello@thetitanstandard.com
        </a>
      </div>

      {/* CTA */}
      <div className="mt-12 text-center">
        <Link to="/#newsletter" className="text-titans-blue hover:text-white font-cond uppercase tracking-wider text-sm inline-flex items-center gap-2">
          Subscribe to the newsletter <ArrowRight size={12} />
        </Link>
      </div>
    </div>
  );
}
